// src/pages/teacher/TeacherStepQuizEditor.jsx
// ویرایشگر آزمون مرحله — wrapper بین CreateExam/CreateExamAI و ExamContext
// ================================================================
import React, { useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import {
  BooksEngineProvider, useBooksEngine, useDraftEditor, useProject,
} from '../../hooks/useBooksEngine.js';
import { useExams } from '../../components/Exams/ExamContext.jsx';
import { Spinner, EmptyState } from '../../components/books/BooksSharedUI.jsx';

import CreateExam from '../../components/Exams/CreateExam.jsx';
import CreateExamAI from '../../components/Exams/CreateExamAI.jsx';

// ================================================================
// ENTRY
// ================================================================
export default function TeacherStepQuizEditor() {
  const { user } = useAuth();
  if (!user || user.role !== 'teacher') {
    return <EmptyState icon="🔒" title="دسترسی محدود" />;
  }
  return (
    <BooksEngineProvider user={user}>
      <Inner />
    </BooksEngineProvider>
  );
}

// ================================================================
// INNER
// ================================================================
function Inner() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const chapterId = searchParams.get('chapterId');
  const lessonId = searchParams.get('lessonId');
  const stepId = searchParams.get('stepId');
  const mode = searchParams.get('mode') || 'manual';

  const project = useProject(projectId);
  const draft = useDraftEditor(projectId);
  const { getStepQuiz, upsertStepQuiz } = useExams();

  // ──── پیدا کردن step از draft ────
  const step = useMemo(() => {
    if (!draft.draft || !chapterId || !lessonId || !stepId) return null;
    const ch = draft.draft.chapters?.find((c) => c._id === chapterId);
    const ls = ch?.lessons?.find((l) => l._id === lessonId);
    return ls?.steps?.find((s) => s._id === stepId) || null;
  }, [draft.draft, chapterId, lessonId, stepId]);

  // ──── اگر step.quizId داره → quiz موجود ────
  const existingQuiz = useMemo(() => {
    if (!step?.quizId || !draft.draft?.id) return null;
    return getStepQuiz({ versionId: draft.draft.id, stepId }) || null;
  }, [step?.quizId, draft.draft?.id, stepId, getStepQuiz]);

  // ──── داده‌ی اولیه ────
  const initialData = useMemo(() => {
    if (existingQuiz) {
      return {
        ...existingQuiz,
        passThreshold: existingQuiz.passThreshold ?? 12,
        maxAttempts: existingQuiz.maxAttempts ?? 2,
        retryPrice: existingQuiz.retryPrice ?? 10000,
      };
    }
    return {
      title: `آزمون: ${step?.title || 'مرحله جدید'}`,
      duration: 30,
      passThreshold: 12,
      maxAttempts: 2,
      retryPrice: 10000,
      questions: [],
      shuffleQuestions: false,
      shuffleOptions: false,
      showResult: true,
      showCorrectAnswers: false,
      displayMode: 'step',
      isStepQuiz: true,
      coinsExcellent: 0, xpExcellent: 0,
      coinsVeryGood: 0, xpVeryGood: 0,
      coinsGood: 0, xpGood: 0,
      coinsNeedsWork: 0, xpNeedsWork: 0,
      coinsUnacceptable: 0, xpUnacceptable: 0,
      coinsFailed: 0, xpFailed: 0,
    };
  }, [existingQuiz, step?.title]);

  // ──── ذخیره‌سازی ────
  const handleSave = useCallback(async (examData) => {
    if (!draft.draft || !project) {
      return { ok: false, errors: ['no-draft-or-project'] };
    }
    if (!chapterId || !lessonId || !stepId) {
      return { ok: false, errors: ['missing-context'] };
    }

    const passThreshold = Number(examData.passThreshold) || 12;
    const maxAttempts = Number(examData.maxAttempts) || 2;
    const retryPrice = Number(examData.retryPrice) || 10000;

    const quizPayload = {
      ...examData,
      isStepQuiz: true,
      isGlobal: false,
      isChallenge: false,
      isDailyChallenge: false,
      teamId: null,
      passThreshold,
      maxAttempts,
      retryPrice,
      limitAttempts: maxAttempts,
      linkContext: {
        projectId,
        versionId: draft.draft.id,
        chapterId,
        lessonId,
        stepId,
        grade: project.grade,
        subject: project.subject,
        field: project.field || null,
        folderName: project.folderName,
        teacherId: project.teacherId,
      },
    };

    const savedQuiz = upsertStepQuiz(quizPayload);
    if (!savedQuiz) return { ok: false, errors: ['quiz_save_failed'] };

    // 🆕 دریافت chapters جدید از updateStep و پاس دادن به forceSave
    // این کار مشکل «stale state در forceSave» را کاملاً حل می‌کند
    const newChapters = draft.updateStep(chapterId, lessonId, stepId, {
      quizId: savedQuiz.id,
      quizMeta: {
        title: quizPayload.title || 'آزمون',
        questionCount: quizPayload.questions?.length || 0,
        duration: quizPayload.duration || 0,
        passThreshold,
        maxAttempts,
        retryPrice,
      },
    });

    // 🆕 ذخیره با chapters جدید — نه state کهنه
    await draft.forceSave(newChapters);

    return { ok: true, data: savedQuiz };
  }, [draft, project, chapterId, lessonId, stepId, projectId, upsertStepQuiz]);

  // ──── لغو ────
  const handleCancel = useCallback(() => {
    // اگر آزمون تازه ساخته شده و quizId نداره، step را پاک کن
    if (step && !step.quizId) {
      draft.removeStep(chapterId, lessonId, stepId);
    }
    navigate(`/teacher/books/edit/${projectId}`);
  }, [draft, step, chapterId, lessonId, stepId, navigate, projectId]);

  // ──── موفقیت ────
  const handleSuccess = useCallback(() => {
    navigate(`/teacher/books/edit/${projectId}`);
  }, [navigate, projectId]);

  // ──── Guards ────
  if (draft.loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spinner size={32} />
      </div>
    );
  }
  if (!draft.draft) return <EmptyState icon="📦" title="پیش‌نویس یافت نشد" />;
  if (!project) return <EmptyState icon="📦" title="پروژه یافت نشد" />;
  if (!step) return <EmptyState icon="📝" title="مرحله یافت نشد" />;

  const useAI = mode === 'ai';

  return useAI ? (
    <CreateExamAI
      __stepQuizContext={{
        initialData,
        onSave: handleSave,
        onCancel: handleCancel,
        onSuccess: handleSuccess,
      }}
    />
  ) : (
    <CreateExam
      __stepQuizContext={{
        initialData,
        onSave: handleSave,
        onCancel: handleCancel,
        onSuccess: handleSuccess,
      }}
    />
  );
}